/** The rows grid's filters (server-side, combined with AND): kind, selection, packages,
 *  upstream change, listing state, category and an effective-setting filter. Kinds, settings
 *  and their values come from the registry. Free-text search lives in the grid toolbar. */
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  ListItemText,
  MenuItem,
  TextField,
} from '@mui/material';
import { useMemo } from 'react';

import type { CatalogueRegistry, CatalogueRowFilters } from '@/api/catalogue';

import { valueLabel } from './catalogueModel';

type Tri = 'all' | 'true' | 'false';

const triOf = (v: boolean | undefined): Tri => (v === undefined ? 'all' : v ? 'true' : 'false');
const boolOf = (t: string): boolean | undefined => (t === 'all' ? undefined : t === 'true');

/** `?setting=key:value` or `key` + `setting_source=ops` ("overridden by ops"). */
const OPS_OVERRIDE = '__ops__';

export function activeFilterCount(f: CatalogueRowFilters): number {
  return [
    f.kind?.length,
    f.category,
    f.package !== undefined,
    f.include !== undefined,
    f.pending !== undefined,
    f.removed !== undefined,
    f.setting,
  ].filter(Boolean).length;
}

function TriSelect({
  label,
  value,
  onChange,
  labels,
  width = 150,
}: {
  label: string;
  value: boolean | undefined;
  onChange: (v: boolean | undefined) => void;
  labels: [string, string, string];
  width?: number;
}) {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={triOf(value)}
      onChange={(e) => onChange(boolOf(e.target.value))}
      sx={{ width }}
    >
      <MenuItem value="all">{labels[0]}</MenuItem>
      <MenuItem value="true">{labels[1]}</MenuItem>
      <MenuItem value="false">{labels[2]}</MenuItem>
    </TextField>
  );
}

export function CatalogueFilters({
  registry,
  value,
  onChange,
  categories,
}: {
  registry: CatalogueRegistry;
  value: CatalogueRowFilters;
  onChange: (next: CatalogueRowFilters) => void;
  /** Category titles seen on the loaded rows (suggestions; any id or title is accepted). */
  categories: string[];
}) {
  const kindSpec = registry.settings.find((s) => s.key === 'kind');
  const kinds = registry.kinds;
  const filterable = useMemo(
    () => registry.settings.filter((s) => s.scope === 'product' && s.key !== 'kind'),
    [registry],
  );
  const [settingKey, settingRaw] = (() => {
    if (!value.setting) return ['', ''];
    const i = value.setting.indexOf(':');
    return i < 0 ? [value.setting, OPS_OVERRIDE] : [value.setting.slice(0, i), value.setting.slice(i + 1)];
  })();
  const settingSpec = filterable.find((s) => s.key === settingKey);
  const settingValues: Array<{ value: string; label: string }> = settingSpec
    ? settingSpec.type === 'bool'
      ? [
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' },
        ]
      : settingSpec.type === 'enum'
        ? settingSpec.values.map((v) => ({
            value: JSON.stringify(v),
            label: valueLabel(settingSpec, v),
          }))
        : []
    : [];

  const setSetting = (key: string, raw: string) => {
    if (!key) {
      onChange({ ...value, setting: undefined, setting_source: undefined });
      return;
    }
    if (raw === OPS_OVERRIDE || raw === '') {
      onChange({ ...value, setting: key, setting_source: 'ops' });
      return;
    }
    // Enum values travel JSON-encoded so `"option"` and `true` are told apart server-side.
    const plain = raw.startsWith('"') ? (JSON.parse(raw) as string) : raw;
    onChange({ ...value, setting: `${key}:${plain}`, setting_source: undefined });
  };
  const settingSelectValue =
    !settingKey ? '' : value.setting_source === 'ops' && !value.setting?.includes(':')
      ? OPS_OVERRIDE
      : settingSpec?.type === 'enum'
        ? JSON.stringify(settingRaw)
        : settingRaw;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
      <TextField
        select
        size="small"
        label="Kind"
        value={value.kind ?? []}
        onChange={(e) => {
          const v = e.target.value as unknown as string[] | string;
          const list = typeof v === 'string' ? v.split(',').filter(Boolean) : v;
          onChange({ ...value, kind: list.length ? list : undefined });
        }}
        SelectProps={{
          multiple: true,
          renderValue: (sel) => {
            const list = sel as string[];
            return list.length === 1 ? valueLabel(kindSpec, list[0]) : `${list.length} kinds`;
          },
        }}
        sx={{ width: 190 }}
      >
        {kinds.map((k) => (
          <MenuItem key={k} value={k} dense>
            <Checkbox size="small" checked={(value.kind ?? []).includes(k)} sx={{ p: 0.25, mr: 1 }} />
            <ListItemText primary={valueLabel(kindSpec, k)} />
          </MenuItem>
        ))}
      </TextField>
      <TriSelect
        label="Selection"
        value={value.include}
        onChange={(include) => onChange({ ...value, include })}
        labels={['All products', 'Included', 'Not included']}
      />
      <TriSelect
        label="Packages"
        value={value.package}
        onChange={(pkg) => onChange({ ...value, package: pkg })}
        labels={['All', 'Packages only', 'No packages']}
        width={140}
      />
      <TriSelect
        label="Upstream"
        value={value.pending}
        onChange={(pending) => onChange({ ...value, pending })}
        labels={['Any', 'Waiting for review', 'Not waiting']}
      />
      <TriSelect
        label="Listing"
        value={value.removed}
        onChange={(removed) => onChange({ ...value, removed })}
        labels={['All', 'Removed upstream', 'Still listed']}
        width={160}
      />
      <Autocomplete
        freeSolo
        size="small"
        options={categories}
        value={value.category ?? ''}
        onChange={(_, v) => onChange({ ...value, category: v ? String(v) : undefined })}
        onInputChange={(_e, _v, reason) => {
          if (reason === 'clear') onChange({ ...value, category: undefined });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Category"
            placeholder="id or title"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const v = (e.target as HTMLInputElement).value.trim();
                onChange({ ...value, category: v || undefined });
              }
            }}
          />
        )}
        sx={{ width: 190 }}
      />
      <TextField
        select
        size="small"
        label="Setting"
        value={settingKey}
        onChange={(e) => setSetting(e.target.value, OPS_OVERRIDE)}
        sx={{ width: 190 }}
      >
        <MenuItem value="">
          <em>Any</em>
        </MenuItem>
        {filterable.map((s) => (
          <MenuItem key={s.key} value={s.key}>
            {s.label}
          </MenuItem>
        ))}
      </TextField>
      {settingKey && (
        <TextField
          select
          size="small"
          label="Is"
          value={settingSelectValue}
          onChange={(e) => setSetting(settingKey, e.target.value)}
          sx={{ width: 200 }}
        >
          <MenuItem value={OPS_OVERRIDE}>Overridden by ops (any value)</MenuItem>
          {settingValues.map((v) => (
            <MenuItem key={v.value} value={v.value}>
              {v.label}
            </MenuItem>
          ))}
        </TextField>
      )}
      {activeFilterCount(value) > 0 && (
        <Button size="small" startIcon={<FilterAltOffIcon fontSize="small" />} onClick={() => onChange({})}>
          Clear filters
        </Button>
      )}
    </Box>
  );
}
